/*
 * Small compatibility fixes for defects in the compiled base-theme bundle.
 * Capture-phase handlers prevent the broken target handlers from running while
 * leaving the generated vendor assets untouched and upgrade-safe.
 */
(function () {
  'use strict';

  var CART_INPUT_DELAY_MS = 1000;
  var quizOpener = null;
  var quickPurchaseStates = new WeakMap();

  function closest(event, selector) {
    var target = event && event.target;
    return target && target.closest ? target.closest(selector) : null;
  }

  function formatMoney(cents) {
    var amount = (Math.max(0, Number(cents) || 0) / 100).toFixed(2).split('.');
    amount[0] = amount[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    var format = window.theme && window.theme.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount.join('.'));
  }

  function syncOptionStates(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-option-buttons]'), function (group) {
      Array.prototype.forEach.call(group.querySelectorAll('[data-button]'), function (button) {
        button.setAttribute('aria-pressed', button.classList.contains('selected') ? 'true' : 'false');
      });
    });
  }

  function handleProductOption(event) {
    var button = closest(event, '[data-option-buttons] [data-button]');
    if (!button) return false;
    setTimeout(function () { syncOptionStates(button.closest('[data-option-buttons]')); }, 0);
    return true;
  }

  function handleAddressRemoval(event) {
    var button = closest(event, '[data-section-type="addresses"] [data-remove]');
    if (!button || !window.Shopify || typeof window.Shopify.postLink !== 'function') return false;

    event.preventDefault();
    event.stopPropagation();

    var message = button.getAttribute('data-confirm-message') || 'Are you sure you wish to delete this address?';
    if (!window.confirm(message)) return true;

    window.Shopify.postLink(button.getAttribute('data-target'), {
      parameters: { _method: 'delete' }
    });
    return true;
  }

  function handleNotifyTrigger(event) {
    var trigger = closest(event, '.avaly-notify__trigger');
    if (!trigger) return false;

    var wrapper = trigger.closest('.avaly-notify');
    var form = wrapper && wrapper.querySelector('.avaly-notify__form');
    if (!form) return false;

    event.preventDefault();
    event.stopPropagation();
    trigger.style.display = 'none';
    trigger.setAttribute('aria-expanded', 'true');
    form.style.display = 'block';
    var input = form.querySelector('.avaly-notify__input');
    if (input) input.focus();
    return true;
  }

  function handleShareToggle(event) {
    var button = closest(event, '[data-social-share]');
    if (!button) return false;

    var menu = button.querySelector('.product__share-icons, .article__share-icons, .password__share-icons');
    if (!menu) return false;

    event.stopPropagation();

    // Let an already-open share destination perform its normal default action.
    if (closest(event, 'a')) return true;

    event.preventDefault();
    var expanded = !button.classList.contains('active');
    button.classList.toggle('active', expanded);
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    menu.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    return true;
  }

  function showQuickPurchaseError(root) {
    var error = root && root.querySelector('[data-quantity-error]');
    if (!error) return;
    error.textContent = 'We could not update your cart. Please try again.';
    error.classList.remove('hidden');
  }

  function handleQuickPurchase(event) {
    var button = closest(event, '[data-quick-purchase-button]');
    if (!button) return false;

    var bar = button.closest('[data-quick-purchase-bar]');
    var product = bar && bar.closest('[data-section-type="product"]');
    var quantityInput = product && (
      product.querySelector('[data-quantity-input]') ||
      product.querySelector('input[name="quantity"]')
    );
    var checkout = bar && bar.querySelector('.quick-purchase-bar__purchase-link');
    var variantId = Number(bar && bar.getAttribute('data-variant-id'));
    var quantity = Math.max(1, parseInt(quantityInput && quantityInput.value, 10) || 1);
    if (!bar || !checkout) return false;

    event.preventDefault();
    event.stopPropagation();
    var state = quickPurchaseStates.get(button);
    if (state && state.pending) return true;
    var cart = window.AvalyCart;
    // Mixed/stale assets must not fall through to the unsafe compiled handoff.
    if (!cart || typeof cart.addVariant !== 'function' || typeof cart.checkout !== 'function') {
      showQuickPurchaseError(product);
      return true;
    }
    if (!Number.isSafeInteger(variantId) || variantId <= 0) {
      showQuickPurchaseError(product);
      return true;
    }
    var signature = variantId + ':' + quantity;
    if (!state || state.signature !== signature) state = { signature: signature, added: false };
    state.pending = true;
    state.inFlight = true;
    quickPurchaseStates.set(button, state);
    button.disabled = true;
    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');

    function variantQuantity(currentCart) {
      if (!currentCart || !Array.isArray(currentCart.items)) throw new Error('Cart could not be confirmed');
      return currentCart.items.reduce(function (total, line) {
        return Number(line.variant_id || line.id) === variantId ? total + Math.max(0, Number(line.quantity) || 0) : total;
      }, 0);
    }

    function addForCheckout() {
      // This control stays busy through checkout; the cart's delayed add-button
      // animation must not unlock it between the mutation and server readback.
      return cart.addVariant(variantId, quantity, null).then(function (savedCart) {
        state.added = true;
        state.confirmedQuantity = savedCart && Array.isArray(savedCart.items) ? variantQuantity(savedCart) : quantity;
      });
    }

    Promise.resolve().then(function () {
      // On a failed checkout readback, retry the confirmed add's handoff rather
      // than adding that same selection twice. Recheck first: the shopper may
      // have removed/reduced the line while reviewing a failed checkout.
      if (state.added) return cart.getCart(true).then(function (currentCart) {
        if (variantQuantity(currentCart) >= state.confirmedQuantity) return;
        state.added = false;
        return addForCheckout();
      });
      return addForCheckout();
    }).then(function () {
      return cart.checkout(checkout.href);
    }).then(function () {
      state.inFlight = false;
    }).catch(function () {
      state.inFlight = false;
      state.pending = false;
      button.disabled = false;
      button.classList.remove('loading');
      button.removeAttribute('aria-busy');
      showQuickPurchaseError(product);
    });
    return true;
  }

  function quizFocusable(overlay) {
    return Array.prototype.filter.call(
      overlay.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'),
      function (element) {
        var inactiveStep = element.closest('.avaly-quiz-step:not(.active)');
        if (element.matches(':disabled') || element.getAttribute('tabindex') === '-1' ||
          inactiveStep || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
        for (var ancestor = element; ancestor; ancestor = ancestor.parentElement) {
          var style = window.getComputedStyle(ancestor);
          if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
        }
        return true;
      }
    );
  }

  function openQuizAccessibility(trigger) {
    var overlay = document.getElementById('avaly-quiz-overlay');
    if (!overlay) return;
    quizOpener = trigger;
    overlay.hidden = false;
    overlay.removeAttribute('inert');
    overlay.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-modal', 'true');
    setTimeout(function () {
      var close = overlay.querySelector('.avaly-quiz-modal__close');
      if (close) close.focus();
    }, 0);
  }

  function closeQuizAccessibility() {
    var overlay = document.getElementById('avaly-quiz-overlay');
    if (!overlay) return;
    setTimeout(function () {
      overlay.hidden = true;
      overlay.setAttribute('inert', '');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.removeAttribute('aria-modal');
      if (quizOpener && document.contains(quizOpener)) quizOpener.focus();
      quizOpener = null;
    }, 50);
  }

  function handleQuizClick(event) {
    var trigger = closest(event, '[data-quiz-trigger]');
    if (trigger) {
      openQuizAccessibility(trigger);
      return false;
    }

    var overlay = document.getElementById('avaly-quiz-overlay');
    if (!overlay || overlay.hidden) return false;
    if (closest(event, '.avaly-quiz-modal__close') || event.target === overlay) {
      closeQuizAccessibility();
    }
    return false;
  }

  function handleClick(event) {
    if (handleAddressRemoval(event)) return;
    if (handleNotifyTrigger(event)) return;
    if (handleQuickPurchase(event)) return;
    if (handleShareToggle(event)) return;
    if (handleProductOption(event)) return;
    handleQuizClick(event);
  }

  function handleQuizKeydown(event) {
    var overlay = document.getElementById('avaly-quiz-overlay');
    if (!overlay || overlay.hidden) return;

    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      var close = overlay.querySelector('.avaly-quiz-modal__close');
      if (close) close.click();
      return;
    }

    if (event.key !== 'Tab' && event.keyCode !== 9) return;
    var focusable = quizFocusable(overlay);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || focusable.indexOf(document.activeElement) === -1)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || focusable.indexOf(document.activeElement) === -1)) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleAdditionalCartQuantity(event) {
    var input = closest(event, '[data-section-type="cart"] [data-quantity-input]');
    if (!input) return;

    var section = input.closest('[data-section-type="cart"]');
    if (!section || section.getAttribute('data-enable-cart-ajax') !== 'true') return;

    var inputs = section.querySelectorAll('[data-quantity-input]');
    // The base theme already binds the first field; cover every repeated row.
    if (!inputs.length || input === inputs[0]) return;

    var form = input.closest('form[data-form]');
    if (!form) return;
    clearTimeout(form._avalyQuantityTimer);
    form._avalyQuantityTimer = setTimeout(function () {
      form.submit();
    }, CART_INPUT_DELAY_MS);
  }

  function setNotifyError(form, message) {
    var wrapper = form.closest('.avaly-notify');
    var error = wrapper && wrapper.querySelector('.avaly-notify__error');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  function clearNotifyError(form) {
    var wrapper = form.closest('.avaly-notify');
    var error = wrapper && wrapper.querySelector('.avaly-notify__error');
    if (error) error.hidden = true;
  }

  function handleNotifySubmit(event) {
    var form = closest(event, '.avaly-notify__form');
    // PDP back-in-stock forms own their Klaviyo handler. Only card capture
    // forms belong to this enhancement; never cancel another form's submit.
    if (!form || !form.closest('.avaly-card') || form.hasAttribute('data-avaly-notify-form')) return;

    event.preventDefault();
    event.stopPropagation();

    var wrapper = form.closest('.avaly-notify');
    var submit = form.querySelector('.avaly-notify__submit');
    var input = form.querySelector('.avaly-notify__input');
    var success = wrapper && wrapper.querySelector('.avaly-notify__success');
    if (!wrapper || !submit || submit.disabled || !input || !input.value.trim()) return;

    clearNotifyError(form);
    submit.disabled = true;
    form.setAttribute('aria-busy', 'true');

    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Notification request failed');
      form.style.display = 'none';
      var trigger = wrapper.querySelector('.avaly-notify__trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (success) {
        success.hidden = false;
        success.style.display = 'flex';
      }
    }).catch(function () {
      form.style.display = 'block';
      submit.disabled = false;
      setNotifyError(form, 'Something went wrong. Please try again.');
      input.focus();
    }).then(function () {
      form.removeAttribute('aria-busy');
    });
  }

  function syncProductVariant(event) {
    var variant = event && event.detail && event.detail.variant;
    setTimeout(function () {
      syncOptionStates(document);
      Array.prototype.forEach.call(document.querySelectorAll('[data-section-type="product"]'), function (product) {
        var addButton = product.querySelector('[data-add-to-cart]');
        if (addButton) {
          var label = addButton.querySelector('[data-add-to-cart-text]');
          addButton.setAttribute('aria-disabled', addButton.disabled ? 'true' : 'false');
          if (label && label.textContent.trim()) addButton.setAttribute('aria-label', label.textContent.trim());
        }

        if (!variant || !Number.isFinite(Number(variant.price))) return;
        var bar = product.querySelector('[data-quick-purchase-bar]');
        var select = product.querySelector('[data-variant-select]');
        var matches = bar && String(bar.getAttribute('data-variant-id')) === String(variant.id);
        matches = matches || (select && String(select.value) === String(variant.id));
        if (!matches) return;
        var instalment = product.querySelector('[data-avaly-instalment]');
        if (instalment) {
          instalment.setAttribute('data-price', String(variant.price));
          instalment.textContent = formatMoney(Number(variant.price) / 4);
        }
      });
    }, 0);
  }

  function refreshQuizResult(result) {
    var sourceLink = result.querySelector('a[href*="/products/"]');
    if (!sourceLink) return;
    var productPath;
    try { productPath = new URL(sourceLink.href, window.location.origin).pathname; }
    catch (_) { return; }
    if (sourceLink.getAttribute('data-avaly-catalog-state') === productPath) return;
    sourceLink.setAttribute('data-avaly-catalog-state', productPath);

    function isCurrentResult() {
      return result.isConnected && result.querySelector('a[href*="/products/"]') === sourceLink &&
        new URL(sourceLink.href, window.location.origin).pathname === productPath;
    }

    fetch(productPath + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Product lookup failed');
        return response.json();
      })
      .then(function (product) {
        if (!isCurrentResult()) return;
        if (!product || !product.handle || !Number.isFinite(Number(product.price))) {
          throw new Error('Invalid product data');
        }
        var canonicalPath = '/products/' + encodeURIComponent(product.handle);
        Array.prototype.forEach.call(result.querySelectorAll('a[href*="/products/"]'), function (link) {
          link.href = canonicalPath;
        });
        var name = result.querySelector('.avaly-quiz-result__product strong, .avaly-pdp-quiz__result-name');
        var price = result.querySelector('.avaly-quiz-result__price, .avaly-pdp-quiz__result-price');
        var cta = result.querySelector('.avaly-quiz-result__atc, .avaly-pdp-quiz__result-cta');
        if (name) name.textContent = product.title;
        if (price) price.textContent = product.available === false ? 'Currently unavailable' : formatMoney(product.price);
        if (cta) {
          if (product.available === false) {
            cta.removeAttribute('href');
            cta.setAttribute('aria-disabled', 'true');
            cta.textContent = 'Currently unavailable';
          } else {
            cta.href = canonicalPath;
            cta.removeAttribute('aria-disabled');
          }
        }
      })
      .catch(function () {
        if (isCurrentResult()) sourceLink.setAttribute('data-avaly-catalog-state', 'error:' + productPath);
      });
  }

  function watchQuizResults() {
    if (!window.MutationObserver) return;
    Array.prototype.forEach.call(document.querySelectorAll('#avaly-quiz-result, #avaly-pdp-quiz-result'), function (result) {
      var observer = new MutationObserver(function () { refreshQuizResult(result); });
      observer.observe(result, { childList: true, subtree: true });
      refreshQuizResult(result);
    });
  }

  document.addEventListener('click', handleClick, true);
  document.addEventListener('keydown', handleQuizKeydown, true);
  document.addEventListener('input', handleAdditionalCartQuantity, true);
  document.addEventListener('submit', handleNotifySubmit, true);
  document.addEventListener('product:variant-change', syncProductVariant);
  watchQuizResults();

  // The base collection controller updates history but does not redraw when
  // Back/Forward changes it. A normal reload keeps URL, filters and products in
  // sync without duplicating the theme's private AJAX renderer.
  window.addEventListener('popstate', function () {
    if (document.querySelector('[data-section-type="collection"]')) {
      window.location.reload();
    }
  });
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-quick-purchase-button]'), function (button) {
      var state = quickPurchaseStates.get(button);
      // A restored document may still have an unresolved cart/checkout promise.
      // Only a settled handoff can release the sticky navigation lock.
      if (!state || state.inFlight) return;
      state.pending = false;
      button.disabled = false;
      button.classList.remove('loading');
      button.removeAttribute('aria-busy');
    });
  });
})();
